/**
 * Chat widget Zustand store.
 * @package @plerity/chat-widget
 */

import { create } from "zustand";
import type { ChatMessage, ChatSession } from "./types";

export interface ChatStore {
  /** Whether the chat panel is open */
  isOpen: boolean;
  /** Conversation messages */
  messages: ChatMessage[];
  /** Whether the assistant is currently generating */
  isStreaming: boolean;
  /** Current partial response being streamed */
  streamingContent: string;
  /** Error message if last request failed */
  error: string | null;
  /** Current session ID for history persistence */
  sessionId: string | null;
  /** Suggested prompts for the current context */
  suggestions: string[];
  /** Saved chat sessions from the server */
  sessions: ChatSession[];
  /** Whether we're loading sessions */
  loadingSessions: boolean;
  /** Whether rate-limited */
  rateLimited: boolean;
  /** Rate limit reset time (epoch ms) */
  rateLimitResetAt: number | null;

  // Actions
  toggle: () => void;
  open: () => void;
  close: () => void;
  addMessage: (message: ChatMessage) => void;
  setStreaming: (streaming: boolean) => void;
  appendStreamContent: (chunk: string) => void;
  clearStreamContent: () => void;
  setError: (error: string | null) => void;
  clearMessages: () => void;
  setSessionId: (id: string | null) => void;
  setSuggestions: (suggestions: string[]) => void;
  setSessions: (sessions: ChatSession[]) => void;
  setLoadingSessions: (loading: boolean) => void;
  setRateLimited: (limited: boolean, resetAt?: number | null) => void;
  loadSession: (session: ChatSession, messages: ChatMessage[]) => void;
  newConversation: () => void;
}

let messageCounter = 0;

export function generateMessageId(): string {
  messageCounter += 1;
  return `msg-${Date.now()}-${messageCounter}`;
}

export const useChatStore = create<ChatStore>((set) => ({
  isOpen: false,
  messages: [],
  isStreaming: false,
  streamingContent: "",
  error: null,
  sessionId: null,
  suggestions: [],
  sessions: [],
  loadingSessions: false,
  rateLimited: false,
  rateLimitResetAt: null,

  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),

  addMessage: (message) => set((s) => ({ messages: [...s.messages, message] })),

  setStreaming: (streaming) => set({ isStreaming: streaming }),

  appendStreamContent: (chunk) => set((s) => ({ streamingContent: s.streamingContent + chunk })),

  clearStreamContent: () => set({ streamingContent: "" }),

  setError: (error) => set({ error }),

  clearMessages: () => set({ messages: [], streamingContent: "", error: null, sessionId: null }),

  setSessionId: (id) => set({ sessionId: id }),

  setSuggestions: (suggestions) => set({ suggestions }),

  setSessions: (sessions) => set({ sessions }),

  setLoadingSessions: (loading) => set({ loadingSessions: loading }),

  setRateLimited: (limited, resetAt = null) => set({ rateLimited: limited, rateLimitResetAt: resetAt }),

  loadSession: (session, messages) =>
    set({
      sessionId: session.id,
      messages,
      streamingContent: "",
      error: null,
    }),

  newConversation: () =>
    set({
      sessionId: null,
      messages: [],
      streamingContent: "",
      error: null,
    }),
}));
